/**
 * A free-entry list (the `taglist` widget): I1's `product_keywords` as text, E's Quantity options as whole numbers.
 * What typing adds to the values already entered. Pure, so the web shell's chip input and any other shell share
 * one rule.
 */

/** What each entry of the list is: free text, a whole number, or any number. */
export type TagItemKind = "text" | "integer" | "number";

export type TagValue = string | number;

export interface TagEntry {
  /** The list after the entry, in the order entered. */
  values: TagValue[];
  /** What was typed but is not a value of the list's kind (a number list given "abc"): not added. */
  rejected: string[];
}

/**
 * The keys that add what is typed so far. A number list does not split on commas: "1,000" is a quantity, so a
 * comma there is a thousands separator, and a space or semicolon separates entries instead.
 */
export function tagSeparatorKeys(kind: TagItemKind): readonly string[] {
  return kind === "text" ? ["Enter", ","] : ["Enter", " ", ";"];
}

/**
 * The list after the person enters `typed`. Text splits on commas (a pasted "a, b, c" adds three), numbers on
 * spaces and semicolons. Each entry is trimmed and blanks dropped; a number entry must parse (commas as thousands
 * separators) and, for a whole-number list, be whole, or it is rejected. A value already in the list is left out:
 * text compared in any case, numbers by value.
 */
export function enterTags(
  current: readonly TagValue[],
  typed: string,
  kind: TagItemKind = "text",
): TagEntry {
  const keyOf = (value: TagValue) =>
    typeof value === "string" ? value.toLowerCase() : String(value);
  const seen = new Set(current.map(keyOf));
  const values = [...current];
  const rejected: string[] = [];
  const parts = kind === "text" ? typed.split(",") : typed.split(/[\s;]+/);
  for (const part of parts) {
    const entry = part.trim();
    if (!entry) continue;
    const value = kind === "text" ? entry : numberOf(entry, kind);
    if (value === null) {
      rejected.push(entry);
      continue;
    }
    if (seen.has(keyOf(value))) continue;
    seen.add(keyOf(value));
    values.push(value);
  }
  return { values, rejected };
}

function numberOf(entry: string, kind: "integer" | "number"): number | null {
  const plain = entry.replace(/,/g, "");
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(plain)) return null;
  const value = Number(plain);
  if (!Number.isFinite(value)) return null;
  return kind === "integer" && !Number.isInteger(value) ? null : value;
}

/** The list without one value. */
export function removeTag(
  current: readonly TagValue[],
  value: TagValue,
): TagValue[] {
  return current.filter((v) => v !== value);
}
